import * as React from 'react';
import {useSelector} from 'react-redux';

import classNames from 'classnames';

import {CueCardCompact} from 'shells/cue-card/cue-card-compact';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {Link} from 'shells/link/link';
import {Body} from 'shells/typography/body';

import {AiFeatureType, LimitedType} from 'nutshell-graphql-types';

import {appHistory} from '../../history';

import {AiFeatureGateModal} from '../ai-home/ai-feature-gate-modal/ai-feature-gate-modal';
import {useGetSessionUser} from '../core/use-get-session-user';
import {useDismissCueCard} from '../glossary/layout/helpers';
import {useGetLimitsByType} from '../limits/use-get-limits-by-type';

import {
    getAgentName,
    SALES_PROCESS_AGENT,
    getSubHeaderIcon,
    isAgentContextLoaded,
    LEAD_RESEARCH_AGENT,
    registerLeadPageTools,
    unregisterAllPageTools,
    registerPipelinePageTools,
} from './helpers';
import type {AgentKey} from './helpers';
import {InteractionModal} from './interaction-modal/interaction-modal';
import type {InteractionData} from './interaction-modal/types';
import {getAgentEntityContext} from './redux/agent-selectors';
import {PIPELINE_PAGE, LEAD_PAGE} from './redux/consts';
import {useTeamAiOpenAgent} from './use-team-ai-open-agent';
import {useTeamAiSidebarToggle} from './use-team-ai-sidebar-toggle';

import {TeamAiAgentList, TeamAiAgentWrapper, TeamAiHeader} from '.';

import styles from './team-ai-sidebar.module.css';

type Props = {
    isSidebarOpen: boolean;
    userCanAccessSetup: boolean;
    onClose: () => void;
};

const TEAM_AI_CUE_CARD_SETTINGS_KEY = 'team-ai-cue-card';

export function TeamAiSidebarHome(props: Props) {
    const {isSidebarOpen} = props;

    const [selectedAgentKey, setSelectedAgentKey] = React.useState<AgentKey | null>(null);
    const [currentView, setCurrentView] = React.useState<'list' | 'agent'>('list');
    const [isInteractionModalOpen, setIsInteractionModalOpen] = React.useState(false);
    const [interactionModalData, setInteractionModalData] = React.useState<any>(null);
    const [hasManuallyNavigated, setHasManuallyNavigated] = React.useState<boolean>(false);
    const [lastResetEntityId, setLastResetEntityId] = React.useState<string | null>(null);
    const [showCueCard, setShowCueCard] = React.useState<boolean>(false);
    const [showOutOfCreditsModal, setShowOutOfCreditsModal] = React.useState<boolean>(false);
    const [aiFeatureType, setAiFeatureType] = React.useState<AiFeatureType>(
        AiFeatureType.EMAIL_SEQUENCE_WRITER
    );

    const agentContext = useSelector(getAgentEntityContext);
    // @ts-expect-error
    const chatbot = agentContext && agentContext.chatbot;
    // @ts-expect-error
    const subHeaderIcon = getSubHeaderIcon(agentContext && agentContext.entityId);

    const {onDismiss} = useDismissCueCard();
    const {user} = useGetSessionUser();
    const setTeamAiSidebarToggle = useTeamAiSidebarToggle();

    const hasAiOutcomes = NutClientConfig.hasAiOutcomesFF;
    const {
        limits: outcomeLimits,
        refetch: refetchLimits,
        isLoading: isLoadingOutcomeLimits,
    } = useGetLimitsByType(hasAiOutcomes ? LimitedType.AI_OUTCOMES : LimitedType.AI_CONVERSATIONS);

    const planLimit = outcomeLimits?.currentPlanLimit;
    const remainingCredits = Math.max(
        planLimit ? planLimit - (outcomeLimits?.currentUsage ?? 0) : 0,
        0
    );

    const canUseAgents = outcomeLimits?.canAdd ?? true;

    const containerStyleNames = classNames(styles['home-container'], {
        [styles['home-container-disabled']]: !hasAiOutcomes && !canUseAgents,
        [styles['home-container-has-cue-card']]: showCueCard && currentView === 'list',
        [styles['home-container-list-view']]: currentView === 'list',
        [styles['home-container-agent-view']]: currentView === 'agent',
    });

    const handleAgentSelected = React.useCallback(
        (agentKey: AgentKey) => {
            if (user && user.id) {
                // Open the sidebar if it's not already open
                if (!isSidebarOpen) {
                    setTeamAiSidebarToggle(user.id, true);
                }
                // Set the agent key (will be applied when sidebar opens or immediately if already open)
                setSelectedAgentKey(agentKey);
                setCurrentView('agent');
            }
        },
        [isSidebarOpen, user, setTeamAiSidebarToggle, setSelectedAgentKey, setCurrentView]
    );

    useTeamAiOpenAgent({
        canUseAgents,
        onAgentSelected: handleAgentSelected,
    });

    React.useEffect(() => {
        // Don't automatically navigate to an agent if the user just
        // manually navigated or doesn't have any credits
        if (hasManuallyNavigated || !canUseAgents) {
            return;
        }

        // @ts-expect-error
        if (agentContext && !selectedAgentKey && agentContext.pageInfo === PIPELINE_PAGE) {
            setSelectedAgentKey(SALES_PROCESS_AGENT);
            setCurrentView('agent');
            // @ts-expect-error
        } else if (agentContext && !selectedAgentKey && agentContext.pageInfo === LEAD_PAGE) {
            setSelectedAgentKey(LEAD_RESEARCH_AGENT);
            setCurrentView('agent');
        } else if (!isAgentContextLoaded(agentContext) && !isSidebarOpen) {
            // Move back to the list view when the sidebar is closed and the
            // agent context is unloaded. This prevents a specific agent view
            // from staying open when the user navigates to a different page.
            setCurrentView('list');
            setSelectedAgentKey(null);
        }

        if (isSidebarOpen && currentView === 'list') {
            refetchLimits();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentContext, isSidebarOpen]);

    // Reset conversation for Lead Agent when navigating to a new Lead page
    React.useEffect(() => {
        if (
            agentContext &&
            chatbot &&
            // @ts-expect-error
            agentContext.pageInfo === LEAD_PAGE &&
            selectedAgentKey === LEAD_RESEARCH_AGENT &&
            // @ts-expect-error
            lastResetEntityId !== agentContext.entityId
        ) {
            chatbot.resetConversation();
            // @ts-expect-error
            setLastResetEntityId(agentContext.entityId);
        }
    }, [agentContext, chatbot, selectedAgentKey, lastResetEntityId]);

    // Page-specific tool registration
    React.useEffect(() => {
        // @ts-expect-error
        if (isAgentContextLoaded(agentContext) && agentContext.chatbot) {
            // @ts-expect-error
            switch (agentContext.pageInfo) {
                case PIPELINE_PAGE:
                    // @ts-expect-error
                    registerPipelinePageTools(agentContext.chatbot);
                    break;
                case LEAD_PAGE:
                    // @ts-expect-error
                    registerLeadPageTools(agentContext.chatbot, handleTriggerInteractionEvent);
                    break;
            }
        }

        // Unregister all page tools from the currently loaded agent
        // @ts-expect-error
        if (!isAgentContextLoaded(agentContext) && agentContext.chatbot) {
            // @ts-expect-error
            unregisterAllPageTools(agentContext.chatbot);
        }
    }, [agentContext]);

    // Check if the user has dismissed the cue card
    React.useEffect(() => {
        if (user && user.dismissedSettings) {
            const isDismissed = user.dismissedSettings.includes(
                `cuehide_${TEAM_AI_CUE_CARD_SETTINGS_KEY}`
            );
            setShowCueCard(!isDismissed);
        }
    }, [user]);

    // Used as a callback for tools that need to trigger an interaction event
    const handleTriggerInteractionEvent = (data: InteractionData) => {
        setIsInteractionModalOpen(true);
        setInteractionModalData(data);
    };

    const handleOpenAgent = (agentKey: AgentKey) => {
        if (hasAiOutcomes && !canUseAgents) {
            switch (agentKey) {
                case 'SALES_PROCESS':
                    setAiFeatureType(AiFeatureType.SALES_PIPELINE_DESIGNER);
                    break;
                case 'EMAIL_SEQUENCE':
                    setAiFeatureType(AiFeatureType.EMAIL_SEQUENCE_WRITER);
                    break;
                case 'SALES_OBJECTION':
                    setAiFeatureType(AiFeatureType.SALES_OBJECTION_COACH);
                    break;
                case 'LEAD_RESEARCH':
                    setAiFeatureType(AiFeatureType.LEAD_RESEARCHER);
                    break;
            }
            setShowOutOfCreditsModal(true);
            return;
        }

        setSelectedAgentKey(agentKey);
        setCurrentView('agent');
    };

    const handleNavigateBack = () => {
        setCurrentView('list');
        setSelectedAgentKey(null);
        refetchLimits();

        // Allows us to detect a manual navigation in our useEffect and prevent
        // automatic navigation back to the agent for the current page
        setHasManuallyNavigated(true);
        setTimeout(() => {
            setHasManuallyNavigated(false);
        }, 50);
    };

    return (
        <>
            <TeamAiHeader
                title={getAgentName(selectedAgentKey)}
                // @ts-expect-error
                subTitle={agentContext && agentContext.entityName}
                subHeaderIcon={subHeaderIcon}
                onClose={props.onClose}
                onNavigateBack={currentView === 'list' ? undefined : handleNavigateBack}
                activeAgent={selectedAgentKey}
                // @ts-expect-error
                pageContextType={agentContext && agentContext.pageInfo}
            />
            {!hasAiOutcomes && !canUseAgents && (
                <div className='px-16 pt-16'>
                    <FlashBanner type='info' hasBorder={true} animate={false}>
                        <div className='full-width flex justify-sb'>
                            <div>
                                You have reached your conversation limit. Upgrade for more credits.
                            </div>
                            <div className='ml-16 flex align-center'>
                                <Link variant='primary' href='/setup/billing/sales'>
                                    Upgrade
                                </Link>
                            </div>
                        </div>
                    </FlashBanner>
                </div>
            )}
            <div className={containerStyleNames}>
                {/* <div className='overflow-auto'> */}
                {currentView === 'list' && (
                    <TeamAiAgentList
                        // @ts-expect-error
                        onSelectAgent={!hasAiOutcomes && !canUseAgents ? null : handleOpenAgent}
                        isLoadingOutcomeLimits={isLoadingOutcomeLimits}
                        userCanAccessSetup={props.userCanAccessSetup}
                    />
                )}
                {currentView === 'agent' && selectedAgentKey && isSidebarOpen && (
                    <TeamAiAgentWrapper
                        agentKey={selectedAgentKey}
                        // @ts-expect-error
                        entityIdContext={agentContext && agentContext.entityId}
                    />
                )}
                {/* </div> */}
            </div>
            {currentView === 'list' && (
                <div className='flex flex-dir-col px-16 pb-16 gap-16'>
                    {showCueCard && (
                        <div className={styles['cue-card-container']}>
                            <CueCardCompact
                                title='How do AI agents work?'
                                thumbnailIllustration='/include/images/onboarding/ai-sparkle-new.svg'
                                thumbnailBackgroundColor='plumLt'
                                cta={{
                                    text: 'Learn more',
                                    url: 'https://support.nutshell.com/en/articles/11402118-ai-ai-agents',
                                    newTab: true,
                                    actionType: 'external-link',
                                }}
                                history={appHistory}
                                onMinimize={() => {
                                    if (onDismiss) {
                                        onDismiss(TEAM_AI_CUE_CARD_SETTINGS_KEY);
                                    }

                                    setShowCueCard(false);
                                }}
                            />
                        </div>
                    )}
                    {!hasAiOutcomes && (
                        <div className='background-white'>
                            <Body color='light' size='small'>
                                Your Nutshell plan includes {planLimit} conversations per month. You
                                have {remainingCredits} remaining.
                            </Body>
                        </div>
                    )}
                </div>
            )}
            {interactionModalData && (
                <InteractionModal
                    isOpen={isInteractionModalOpen}
                    onClose={() => setIsInteractionModalOpen(false)}
                    interactionModalData={interactionModalData}
                />
            )}
            <AiFeatureGateModal
                isOpen={showOutOfCreditsModal}
                onClose={() => setShowOutOfCreditsModal(false)}
                reason='outOfCredits'
                aiFeatureType={aiFeatureType}
            />
        </>
    );
}
